import {
  EventSearchParameters,
  EventServiceNg2,
  FutureEventLoader,
  PastEventLoader,
} from '../entities/event/event.service';
import { Component, computed, HostListener, OnDestroy, OnInit, signal } from '@angular/core';
import { ContextService } from 'app/services/context.service';
import { SportService, SportTypeStr } from 'app/services/sport/sport.service';
import { UtilsService } from 'app/services/utils.service';
import { Event } from 'app/entities/event/event.types';

type SearchFilters = {
  meta: {
    version: number;
  };
  sportIds: Record<SportTypeStr, boolean>;
};

class EventSearchComponentState {
  queryString: string;
  sportIds: string[];
  loadedFuturePage: number;
  loadedPastPage: number;
  timestamp: Date;
  selectedTabIndex: number;
  scrollPosition: number;

  constructor(
    queryString: string,
    sportIds: string[],
    loadedFuturePage: number,
    loadedPastPage: number,
    selectedTabIndex: number,
    scrollPosition: number
  ) {
    this.queryString = queryString;
    this.sportIds = sportIds;
    this.loadedFuturePage = loadedFuturePage;
    this.loadedPastPage = loadedPastPage;
    this.selectedTabIndex = selectedTabIndex;
    this.scrollPosition = scrollPosition;
    this.timestamp = new Date();
  }
}

const XS_MAX_WIDTH: number = 576;
const STATE_VALITITY_TIME_MSEC = 10 * 60 * 1000; // 10 min

@Component({
  templateUrl: 'eventsearch.html',
  styles: [
    `
      .nav-tabs {
        .nav-item {
          a.nav-link.live-events-tab-header.active {
            color: var(--red);
          }
        }
      }
      .event-search-nav-tabs {
        margin-bottom: 6px;
      }
      .event-search-input {
        padding-right: 40px;
      }
    `,
  ],
  standalone: false
})
export class EventSearchComponent implements OnInit, OnDestroy {
  allLiveEvents = signal<Event[]>([]);
  selectedSportIds = computed<string[]>(() => {
    return Object.keys(this.filters().sportIds).filter((sportId) => {
      return this.filters().sportIds[sportId];
    }) as SportTypeStr[];
  });
  liveEvents = computed<Event[]>(() => {
    return this.allLiveEvents()
      .filter((event) => {
        const sportIds = this.selectedSportIds();
        return sportIds.length == 0 || event.categories.some((c) => sportIds.includes(c.sportType));
      })
      .filter((event) => {
        const searchString = this.searchString()?.toLocaleLowerCase();
        return !searchString || event.name.toLowerCase().includes(searchString.toLowerCase());
      });
  });
  searchString = signal<string>('');
  filters = signal<SearchFilters>(this.emptyFilters());
  futureEvents: Event[];
  pastEvents: Event[];
  futureLoader: FutureEventLoader;
  pastLoader: PastEventLoader;
  loadingLive: boolean;
  doneTypingInterval: number;
  selectedTabIndex: number;
  availableSportTypes: any;
  counter: number;
  showScrollUpAfter: number;
  scrollUpTarget: any;
  typingTimer: any;
  availableSportIds: string[];
  lastScrollPosition: number;
  initialized = false;

  constructor(
    private eventService: EventServiceNg2,
    private contextService: ContextService,
    private sportService: SportService,
    private utilsService: UtilsService
  ) {}

  ngOnInit() {
    this.futureEvents = [];
    this.pastEvents = [];

    this.futureLoader = new FutureEventLoader(this.eventService, this.futureEvents);
    this.pastLoader = new PastEventLoader(this.eventService, this.pastEvents);

    this.loadingLive = false;

    this.doneTypingInterval = 500;
    this.selectedTabIndex = 0;

    this.availableSportTypes = [];

    this.counter = 0;

    this.loadFilters();

    this.loadLiveEvents();
    this.loadSportTypes();
    const savedState: EventSearchComponentState = this.contextService.get('eventSearchComponentState');

    if (savedState && new Date().getTime() - savedState.timestamp.getTime() <= STATE_VALITITY_TIME_MSEC) {
      this.searchString.set(savedState.queryString);
      Promise.all([
        this.futureLoader.loadFromState(savedState.queryString, savedState.sportIds, savedState.loadedFuturePage),
        this.pastLoader.loadFromState(savedState.queryString, savedState.sportIds, savedState.loadedPastPage),
      ])
        .then(() => setTimeout(() => this.scrollVertically(savedState.scrollPosition), 50))
        .finally(() => (this.initialized = true));
      this.selectedTabIndex = savedState.selectedTabIndex;
      this.scrollVertically(savedState.scrollPosition);
    } else {
      this.futureLoader
        .load(new EventSearchParameters(null, this.selectedSportIds()))
        .then(() => this.updateTabSelection());
      this.pastLoader
        .load(new EventSearchParameters(null, this.selectedSportIds()))
        .then(() => this.updateTabSelection());
      this.initialized = true;
    }

    this.showScrollUpAfter = 600;
    this.scrollUpTarget = 500;
    if (window.innerWidth <= XS_MAX_WIDTH) {
      this.showScrollUpAfter = 450;
      this.scrollUpTarget = 200;
    }
  }

  ngOnDestroy() {
    this.saveComponentState();
  }

  @HostListener('window:scroll')
  onScroll() {
    this.lastScrollPosition = window.scrollY;
  }

  scrollVertically(yPosition) {
    window.scroll({ left: 0, top: yPosition, behavior: 'smooth' });
  }

  loadMoreFutureEvents() {
    return this.futureLoader.loadNextPage();
  }

  loadMorePastEvents() {
    return this.pastLoader.loadNextPage();
  }

  saveFilters() {
    const searchFilters = JSON.stringify(this.filters());
    localStorage.setItem('searchFilters', searchFilters);
  }

  emptyFilters(): SearchFilters {
    return {
      meta: {
        version: 1,
      },
      sportIds: {} as Record<SportTypeStr, boolean>,
    };
  }

  loadFilters() {
    const loadedSearchFilters = JSON.parse(localStorage.getItem('searchFilters'));

    if (loadedSearchFilters?.meta) {
      this.filters.set(loadedSearchFilters);
    }
  }

  loadSportTypes() {
    Promise.resolve(this.sportService.types()).then((res) => {
      const sportIds = Object.values(res)
        .filter((value: any) => {
          return value.id !== undefined;
        })
        .map((sport: any) => {
          return sport.id;
        });
      this.availableSportIds = this.utilsService.sortByLocalizedStrings(sportIds, (id) => {
        return 'sport.' + id;
      });
    });
  }

  onSelectedSportsChange(newSportIds: Record<string, boolean>) {
    this.filters.set({
      ...this.filters(),
      sportIds: newSportIds,
    });
    this.search();
    this.saveFilters();
  }

  saveComponentState() {
    const previousStateInfo = this.contextService.get('previousStateInfo');
    this.contextService.set(
      'eventSearchComponentState',
      new EventSearchComponentState(
        this.searchString(),
        this.selectedSportIds(),
        this.futureLoader.loadedPage,
        this.pastLoader.loadedPage,
        this.selectedTabIndex,
        previousStateInfo?.scrollY ?? this.lastScrollPosition
      )
    );
  }

  onSearchChange() {
    clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => this.search(), this.doneTypingInterval);
  }

  isRegistrationOpen(event) {
    const now = new Date();
    return now < event.registrationEndDate2 && now >= event.registrationStartDate;
  }

  loadLiveEvents(clearExisting: boolean = false) {
    if (this.loadingLive) return;
    if (clearExisting) {
      this.allLiveEvents.set([]);
    }
    this.loadingLive = true;
    this.eventService
      .getLive()
      .then((events) => {
        this.allLiveEvents.set(events);
        this.updateTabSelection();
      })
      .catch((error) => this.onError(error))
      .finally(() => (this.loadingLive = false));
  }

  search() {
    if (!this.initialized) {
      return;
    }
    this.futureLoader.load(new EventSearchParameters(this.searchString(), this.selectedSportIds()));
    this.pastLoader.load(new EventSearchParameters(this.searchString(), this.selectedSportIds()));
  }

  updateTabSelection() {
    if (!this.futureLoader.loading && this.loadingLive === false && !this.pastLoader.loading) {
      if (this.selectedTabIndex === 0 && this.liveEvents().length === 0) {
        this.selectedTabIndex = 1;
      }
    }
  }

  onError(error) {
    // AlertService.error(error.data.message);
  }
}
